// Polished chat-notification panel — mirrors the task-notification panel's
// visual language (white bg, forest-green accents, sticky section headers,
// hover action group, animated entry) but reads its data from the live chat
// unread Redux slice.
//
// Designed to live inside a Radix Popover wrapper (NotificationBell in the
// sidebar, mobile-popover in Chat.tsx); the wrapper is intentionally bare so
// this component owns the entire header / body / footer chrome.
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Clock,
  ExternalLink,
  MessageSquare,
  Trash2,
  Volume2,
  VolumeX,
} from 'lucide-react';
import {
  clearConversation,
  clearAll,
  clearHistory,
  removeHistoryEntry,
  selectUnreadEntries,
  selectAllPanelEntries,
  type ChatPanelEntry,
} from '../../redux/slices/chatUnread';
import { toggleMute, selectMuted } from '../../redux/slices/convPrefs';
import {
  selectSoundOnReceive,
  setSoundOnReceive,
} from '../../redux/slices/chatPreferences';
import { playReceiveSound } from '../../utils/chat/sounds';
import * as chatApi from '../../api/chat';

// ── Helpers ──────────────────────────────────────────────────────────────────

// Fire-and-forget mark-seen so the backend doesn't resurrect the badge on
// next listConversations(). UI never waits.
function syncReadOnServer(conversationId: string): void {
  chatApi.markSeen(conversationId, []).catch(() => { /* offline */ });
}

const AVATAR_PALETTE = [
  { bg: '#d1fae5', fg: '#065f46' },
  { bg: '#dbeafe', fg: '#1e40af' },
  { bg: '#fef3c7', fg: '#92400e' },
  { bg: '#fce7f3', fg: '#9d174d' },
  { bg: '#ede9fe', fg: '#4c1d95' },
  { bg: '#ccfbf1', fg: '#134e4a' },
  { bg: '#ffedd5', fg: '#7c2d12' },
];

function avatarColors(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('');
}

function formatTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(diff / 3_600_000);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(diff / 86_400_000);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

type TimeBucket = 'today' | 'yesterday' | 'earlier';
function bucketFor(iso: string): TimeBucket {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 86_400_000;
  const t = d.getTime();
  if (t >= startOfToday) return 'today';
  if (t >= startOfYesterday) return 'yesterday';
  return 'earlier';
}

// ── Hover action button (same look as task panel's ActionButton) ─────────────

interface ActionButtonProps {
  title: string;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  danger?: boolean;
  active?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({ title, onClick, children, danger, active }) => {
  const base: React.CSSProperties = {
    background: active ? 'var(--gt-prio-urgent-bg, #fee2e2)' : 'transparent',
    color: active ? 'var(--gt-prio-urgent-fg, #991b1b)' : 'var(--gt-text-tertiary, #6b7280)',
    transition: 'background 120ms, color 120ms',
  };
  return (
    <button
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      className="p-1.5 rounded-lg"
      style={base}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        el.style.background = danger
          ? 'var(--gt-prio-urgent-bg, #fee2e2)'
          : 'var(--gt-surface-2, #f3f4f6)';
        el.style.color = danger
          ? 'var(--gt-prio-urgent-fg, #991b1b)'
          : 'var(--gt-text-primary, #111827)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget;
        el.style.background = active ? 'var(--gt-prio-urgent-bg, #fee2e2)' : 'transparent';
        el.style.color = active
          ? 'var(--gt-prio-urgent-fg, #991b1b)'
          : 'var(--gt-text-tertiary, #6b7280)';
      }}
    >
      {children}
    </button>
  );
};

// ── Component ────────────────────────────────────────────────────────────────

interface Props {
  onItemSelected: () => void;
}

const PANEL_BG = '#ffffff';
const ACCENT = '#1f4d3f'; // forest green
type ViewMode = 'unread' | 'all';
const VIEW_MODE_KEY = 'gc.chatNotifications.viewMode.v1';

function getStoredViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'unread';
  return window.localStorage.getItem(VIEW_MODE_KEY) === 'all' ? 'all' : 'unread';
}

export default function ChatNotificationsPanel({ onItemSelected }: Props) {
  const unreadEntries = useSelector(selectUnreadEntries);
  const allEntries = useSelector(selectAllPanelEntries);
  const mutedIds = useSelector(selectMuted);
  const soundOn = useSelector(selectSoundOnReceive);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode);

  const mutedSet = useMemo(() => new Set(mutedIds), [mutedIds]);
  const entries: ChatPanelEntry[] = viewMode === 'unread' ? unreadEntries : allEntries;
  const unreadCount = unreadEntries.length;

  const changeViewMode = (mode: ViewMode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
    if (typeof window !== 'undefined') window.localStorage.setItem(VIEW_MODE_KEY, mode);
  };

  const bucketed = useMemo(() => {
    const buckets: Record<TimeBucket, ChatPanelEntry[]> = { today: [], yesterday: [], earlier: [] };
    for (const e of entries) buckets[bucketFor(e.lastActivity)].push(e);
    return buckets;
  }, [entries]);

  // ── Action handlers ────────────────────────────────────────────────────────

  const handleOpen = (e: ChatPanelEntry) => {
    const url = e.lastMessageId
      ? `/chat?open=${encodeURIComponent(e.conversationId)}&msg=${encodeURIComponent(e.lastMessageId)}`
      : `/chat?open=${encodeURIComponent(e.conversationId)}`;
    navigate(url);
    // Only mark-read if it's currently unread (history rows are already read).
    if (!e.readAt) {
      dispatch(clearConversation(e.conversationId));
      syncReadOnServer(e.conversationId);
    }
    onItemSelected();
  };

  const handleMarkRead = (convId: string) => {
    syncReadOnServer(convId);
    dispatch(clearConversation(convId));
  };

  const handleMarkAllRead = () => {
    for (const e of unreadEntries) syncReadOnServer(e.conversationId);
    dispatch(clearAll());
  };

  const handleClearAll = () => {
    const msg = viewMode === 'all'
      ? 'Clear all notifications, including read history?'
      : 'Clear all unread notifications?';
    if (typeof window !== 'undefined' && !window.confirm(msg)) return;
    for (const e of unreadEntries) syncReadOnServer(e.conversationId);
    dispatch(clearAll());
    if (viewMode === 'all') dispatch(clearHistory());
  };

  const handleDeleteHistory = (convId: string) => {
    dispatch(removeHistoryEntry(convId));
  };

  const handleToggleMute = (convId: string) => {
    dispatch(toggleMute(convId));
  };

  const toggleSound = () => {
    const next = !soundOn;
    dispatch(setSoundOnReceive(next));
    if (next) playReceiveSound();
  };

  // ── Render: row ────────────────────────────────────────────────────────────

  const renderRow = (e: ChatPanelEntry, stagger: number) => {
    const colors = avatarColors(e.name);
    const muted = mutedSet.has(e.conversationId);
    const isRead = !!e.readAt;
    return (
      <div
        key={`${e.conversationId}:${isRead ? 'h' : 'u'}`}
        className="group/n relative gt-row-in"
        style={{
          background: '#ffffff',
          borderBottom: '1px solid rgba(15, 45, 31, 0.06)',
          animationDelay: `${stagger * 28}ms`,
        }}
      >
        {/* Accent bar — only shown for unread rows */}
        {!isRead && (
          <div
            className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full"
            style={{ background: ACCENT }}
          />
        )}

        <div
          className="relative flex gap-2.5 px-3 py-3 transition-colors"
          style={{ opacity: isRead ? 0.78 : 1 }}
          onMouseEnter={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = '#f5faf7'; }}
          onMouseLeave={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
        >
          {/* Avatar */}
          <div
            className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-[12px] font-bold"
            style={{
              background: colors.bg,
              color: colors.fg,
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            }}
          >
            {initials(e.name) || '·'}
          </div>

          <div className="flex-1 min-w-0">
            <div className="relative">
              <button
                onClick={() => handleOpen(e)}
                className="text-left w-full min-w-0 block"
                style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer' }}
              >
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className="text-[13.5px] leading-snug gt-clamp-2"
                    style={{
                      fontWeight: isRead ? 600 : 700,
                      color: '#0f172a',
                      letterSpacing: '-0.005em',
                      flex: '1 1 100%',
                      minWidth: 0,
                    }}
                  >
                    {e.name}
                  </span>
                  {!isRead && (
                    <span
                      className="shrink-0 text-[10px] font-bold px-2 py-[2px] rounded-full"
                      style={{
                        background: 'var(--gt-surface-3, #e5e7eb)',
                        color: 'var(--gt-text-secondary, #4b5563)',
                      }}
                    >
                      {e.count > 99 ? '99+' : `×${e.count}`}
                    </span>
                  )}
                  {isRead && (
                    <span
                      className="shrink-0 text-[10px] font-semibold px-2 py-[2px] rounded-full inline-flex items-center gap-1"
                      style={{
                        background: '#e5e7eb',
                        color: '#4b5563',
                        letterSpacing: '0.04em',
                      }}
                    >
                      <Check size={9} /> READ
                    </span>
                  )}
                  {muted && (
                    <span
                      className="shrink-0 text-[10px] font-semibold px-2 py-[2px] rounded-full inline-flex items-center gap-1"
                      style={{
                        background: '#fee2e2',
                        color: '#991b1b',
                        letterSpacing: '0.04em',
                      }}
                    >
                      <BellOff size={9} /> MUTED
                    </span>
                  )}
                  {!isRead && (
                    <span className="shrink-0 relative inline-flex h-1.5 w-1.5">
                      <span
                        className="absolute inline-flex h-full w-full rounded-full animate-ping"
                        style={{ background: ACCENT, opacity: 0.45 }}
                      />
                      <span
                        className="relative inline-flex h-1.5 w-1.5 rounded-full"
                        style={{ background: ACCENT }}
                      />
                    </span>
                  )}
                </div>
                <p
                  className="text-[12.5px] mt-1 gt-clamp-3"
                  style={{ color: '#374151', lineHeight: '1.5', fontWeight: 450 }}
                  title={e.lastContent ?? undefined}
                >
                  {e.lastContent
                    ? e.count > 1
                      ? `${e.lastContent} · +${e.count - 1} more`
                      : e.lastContent
                    : `${e.count} new message${e.count === 1 ? '' : 's'}`}
                </p>
                <span
                  className="text-[11.5px] mt-1.5 inline-flex items-center gap-1.5"
                  style={{ color: '#475569', fontStyle: 'italic', fontWeight: 500 }}
                >
                  <Clock size={11} strokeWidth={2} />
                  {formatTime(e.lastActivity)}
                </span>
              </button>

              {/* Hover action group with frosted backdrop */}
              <div
                className="absolute top-0 right-0 flex items-center gap-0.5 opacity-0 group-hover/n:opacity-100 transition-opacity rounded-lg"
                style={{
                  background: 'rgba(255,255,255,0.94)',
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 1px 4px rgba(15, 45, 31, 0.10)',
                  padding: 2,
                }}
              >
                <ActionButton
                  title={muted ? 'Unmute conversation' : 'Mute conversation'}
                  onClick={() => handleToggleMute(e.conversationId)}
                  active={muted}
                >
                  {muted ? <BellOff size={14} /> : <Bell size={14} />}
                </ActionButton>
                {!isRead && (
                  <ActionButton title="Mark as read" onClick={() => handleMarkRead(e.conversationId)}>
                    <Check size={14} />
                  </ActionButton>
                )}
                <ActionButton title="Open chat" onClick={() => handleOpen(e)}>
                  <ExternalLink size={14} />
                </ActionButton>
                {isRead && (
                  <ActionButton
                    title="Remove from history"
                    onClick={() => handleDeleteHistory(e.conversationId)}
                    danger
                  >
                    <Trash2 size={14} />
                  </ActionButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBucket = (label: string, list: typeof entries, baseStagger: { i: number }) => {
    if (list.length === 0) return null;
    return (
      <div key={label}>
        <div
          className="sticky top-0 z-10 px-4 py-2 flex items-baseline gap-2.5"
          style={{
            background: '#f5f9f7',
            borderBottom: '1px solid rgba(15, 45, 31, 0.06)',
          }}
        >
          <span
            className="text-[10.5px] font-extrabold uppercase"
            style={{ color: '#0f3a2a', letterSpacing: '0.12em' }}
          >
            {label}
          </span>
          <span
            className="text-[11px]"
            style={{ color: '#475569', fontStyle: 'italic', fontWeight: 500 }}
          >
            {list.length} {list.length === 1 ? 'item' : 'items'}
          </span>
        </div>
        {list.map(e => renderRow(e, Math.min(baseStagger.i++, 10)))}
      </div>
    );
  };

  // ── Render: empty ──────────────────────────────────────────────────────────

  if (entries.length === 0) {
    return (
      <div style={{ background: PANEL_BG }} className="flex flex-col">
        <Header
          unreadCount={unreadCount}
          soundOn={soundOn}
          onToggleSound={toggleSound}
          viewMode={viewMode}
          onChangeViewMode={changeViewMode}
        />
        <div className="text-center py-14 px-6">
          <div
            className="relative w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ background: '#e8f3ed', color: ACCENT }}
          >
            <MessageSquare size={22} strokeWidth={2} />
          </div>
          <p
            className="text-[13.5px] font-bold"
            style={{ color: 'var(--gt-text-primary, #0e2a1f)', letterSpacing: '-0.01em' }}
          >
            {viewMode === 'unread' ? 'All caught up' : 'Nothing here yet'}
          </p>
          <p
            className="text-[12px] mt-1"
            style={{ color: 'rgba(15, 45, 31, 0.55)', fontStyle: 'italic' }}
          >
            {viewMode === 'unread'
              ? 'No new messages right now.'
              : 'Past chat notifications will appear here.'}
          </p>
          {viewMode === 'unread' && (
            <button
              onClick={() => changeViewMode('all')}
              className="text-[12px] font-semibold mt-4 px-4 py-1.5 rounded-full"
              style={{ background: ACCENT, color: '#ffffff', border: 0, cursor: 'pointer' }}
            >
              View all notifications
            </button>
          )}
        </div>
        <StyleBlock />
      </div>
    );
  }

  // ── Render: list ───────────────────────────────────────────────────────────

  const staggerRef = { i: 0 };
  return (
    <div style={{ background: PANEL_BG }} className="flex flex-col">
      <Header
        unreadCount={unreadCount}
        soundOn={soundOn}
        onToggleSound={toggleSound}
        viewMode={viewMode}
        onChangeViewMode={changeViewMode}
      />

      <div className="overflow-y-auto" style={{ maxHeight: 460, background: PANEL_BG }}>
        <div key={viewMode} className="gt-list-in">
          {renderBucket('Today', bucketed.today, staggerRef)}
          {renderBucket('Yesterday', bucketed.yesterday, staggerRef)}
          {renderBucket('Earlier', bucketed.earlier, staggerRef)}
        </div>
      </div>

      {/* Footer: Clear all + Mark all as read (only when there's unread) */}
      <div
        className="px-4 py-2.5 flex items-center justify-between gap-2"
        style={{ background: PANEL_BG, borderTop: '1px solid rgba(15, 45, 31, 0.08)' }}
      >
        <button
          onClick={handleClearAll}
          className="px-3 py-1.5 text-[12px] font-semibold rounded-full inline-flex items-center gap-2"
          style={{
            color: 'var(--gt-prio-urgent-fg, #991b1b)',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
          }}
          onMouseEnter={(ev) => { (ev.currentTarget as HTMLButtonElement).style.background = 'var(--gt-prio-urgent-bg, #fee2e2)'; }}
          onMouseLeave={(ev) => { (ev.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
        >
          <Trash2 size={13} />
          Clear all
        </button>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="px-4 py-1.5 text-[12px] font-semibold rounded-full inline-flex items-center gap-2"
            style={{
              background: ACCENT,
              color: '#ffffff',
              border: 0,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(31, 77, 63, 0.24)',
            }}
          >
            <CheckCheck size={14} />
            Mark all as read
          </button>
        )}
      </div>

      <StyleBlock />
    </div>
  );
}

// ── Header (shared between empty + populated states) ─────────────────────────

const Header: React.FC<{
  unreadCount: number;
  soundOn: boolean;
  onToggleSound: () => void;
  viewMode: ViewMode;
  onChangeViewMode: (mode: ViewMode) => void;
}> = ({ unreadCount, soundOn, onToggleSound, viewMode, onChangeViewMode }) => (
  <div
    className="relative px-5 py-4"
    style={{ background: PANEL_BG, borderBottom: '1px solid rgba(15, 45, 31, 0.08)' }}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center"
          style={{
            background: '#e8f3ed',
            border: '1px solid rgba(15, 45, 31, 0.10)',
            color: ACCENT,
          }}
        >
          <MessageSquare size={18} strokeWidth={2.25} />
        </div>
        <div>
          <h2
            className="text-[16px] font-bold leading-tight"
            style={{ color: '#0c2a1f', letterSpacing: '-0.018em' }}
          >
            Chat notifications
          </h2>
          <p
            className="text-[11.5px] mt-0.5"
            style={{ color: ACCENT, fontStyle: 'italic', fontWeight: 500, opacity: 0.85 }}
          >
            {unreadCount === 0
              ? 'All caught up'
              : `${unreadCount} unread ${unreadCount === 1 ? 'chat' : 'chats'}`}
          </p>
        </div>
      </div>
      <button
        onClick={onToggleSound}
        title={soundOn ? 'Mute sound' : 'Enable sound'}
        className="p-2 rounded-xl"
        style={{
          color: 'rgba(15, 45, 31, 0.72)',
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
          transition: 'background 120ms, color 120ms',
        }}
        onMouseEnter={(ev) => {
          const el = ev.currentTarget as HTMLButtonElement;
          el.style.background = 'rgba(15, 45, 31, 0.08)';
          el.style.color = '#0e2a1f';
        }}
        onMouseLeave={(ev) => {
          const el = ev.currentTarget as HTMLButtonElement;
          el.style.background = 'transparent';
          el.style.color = 'rgba(15, 45, 31, 0.72)';
        }}
      >
        {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
      </button>
    </div>

    {/* Unread / All toggle pill — mirrors task notifications panel */}
    <div
      className="relative inline-flex mt-3 p-1 rounded-full"
      style={{
        background: 'rgba(15, 45, 31, 0.10)',
        border: '1px solid rgba(15, 45, 31, 0.08)',
      }}
    >
      <span
        aria-hidden
        className="absolute top-1 bottom-1 rounded-full"
        style={{
          background: '#ffffff',
          width: 'calc(50% - 4px)',
          left: 4,
          transform: viewMode === 'all' ? 'translateX(100%)' : 'translateX(0)',
          transition: 'transform 320ms cubic-bezier(0.33, 1, 0.68, 1)',
          boxShadow: '0 1px 3px rgba(15, 45, 31, 0.18)',
        }}
      />
      <button
        onClick={() => onChangeViewMode('unread')}
        className="relative z-10 px-4 py-1 text-[11px] font-semibold rounded-full min-w-[88px]"
        style={{
          color: viewMode === 'unread' ? ACCENT : 'rgba(15, 45, 31, 0.55)',
          letterSpacing: '0.02em',
          transition: 'color 220ms cubic-bezier(0.33, 1, 0.68, 1)',
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
        }}
      >
        Unread{unreadCount > 0 ? ` · ${unreadCount}` : ''}
      </button>
      <button
        onClick={() => onChangeViewMode('all')}
        className="relative z-10 px-4 py-1 text-[11px] font-semibold rounded-full min-w-[88px]"
        style={{
          color: viewMode === 'all' ? ACCENT : 'rgba(15, 45, 31, 0.55)',
          letterSpacing: '0.02em',
          transition: 'color 220ms cubic-bezier(0.33, 1, 0.68, 1)',
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
        }}
      >
        All
      </button>
    </div>
  </div>
);

// ── Animation styles (scoped via global class names — same as task panel) ────

const StyleBlock = () => (
  <style>{`
    .gt-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    .gt-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
    @keyframes gt-fade-up {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes gt-fade-up-row {
      from { opacity: 0; transform: translateY(6px) scale(0.985); }
      to   { opacity: 1; transform: translateY(0)  scale(1); }
    }
    .gt-list-in { animation: gt-fade-up 220ms cubic-bezier(0.33, 1, 0.68, 1) both; }
    .gt-row-in  { animation: gt-fade-up-row 320ms cubic-bezier(0.33, 1, 0.68, 1) both; will-change: opacity, transform; }
    @media (prefers-reduced-motion: reduce) {
      .gt-list-in, .gt-row-in { animation: none !important; }
    }
  `}</style>
);
